import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams, matchPath } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';
import ChangePasswordModal from './ChangePasswordModal';
import AlertDialog from './AlertDialog';

/**
 * Single shell for all INVESTOR routes: fixed full-width nav (w-52) + outlet.
 */
const InvestorLayout = ({ children }) => {
	const { user, logout } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const { tenantId } = useParams();
	const tenant = tenantId || user?.tenantIdentifier || 'default';
	const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
	const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });

	const path = location.pathname;
	const networkSection = new URLSearchParams(location.search).get('section');

	const onBilling = !!matchPath({ path: '/:tenantId/billing', end: true }, path);
	const onDashboard = !!matchPath({ path: '/:tenantId/investor/dashboard', end: true }, path);
	const onDocuments = !!matchPath({ path: '/:tenantId/investor/documents', end: true }, path);
	const onEvents = !!matchPath({ path: '/:tenantId/investor/events', end: true }, path);
	const onNetworkList = !!matchPath({ path: '/:tenantId/investor/networks', end: true }, path);
	const onNetworkDetail = !!matchPath({ path: '/:tenantId/investor/networks/:id', end: true }, path);
	const onStartupDetail = !!matchPath({ path: '/:tenantId/investor/startups/:id', end: true }, path);

	const onNetwork = (onNetworkList && networkSection !== 'startups') || onNetworkDetail;
	const onStartups = (onNetworkList && networkSection === 'startups') || onStartupDetail;

	const navClass = (active) =>
		`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all duration-150 ${
			active
				? 'bg-indigo-50 text-indigo-700 font-medium shadow-sm'
				: 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
		}`;

	const items = [
		{
			id: 'portfolio',
			label: 'My Portfolio',
			icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
			active: onDashboard,
			onClick: () => navigate(`/${tenant}/investor/dashboard`)
		},
		{
			id: 'network',
			label: 'Network',
			icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
			active: onNetwork,
			onClick: () => navigate(`/${tenant}/investor/networks`)
		},
		{
			id: 'startups',
			label: 'Startups',
			icon: 'M13 10V3L4 14h7v7l9-11h-7z',
			active: onStartups,
			onClick: () => navigate(`/${tenant}/investor/networks?section=startups`)
		},
		{
			id: 'documents',
			label: 'Documents',
			icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
			active: onDocuments,
			onClick: () => navigate(`/${tenant}/investor/documents`)
		},
		{
			id: 'events',
			label: 'Events',
			icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
			active: onEvents,
			onClick: () => navigate(`/${tenant}/investor/events`)
		}
	];

	const handleLogout = () => {
		logout();
		navigate('/login');
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex">
			<aside className="fixed left-0 top-0 h-full w-52 z-40 flex flex-col bg-white/95 backdrop-blur-sm border-r border-slate-200/80 shadow-sm">
				<div className="h-14 flex items-center shrink-0 px-3 border-b border-slate-100">
					<Logo width={36} height={36} showText={true} className="[&_span]:!text-lg" />
				</div>

				<nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
					{items.map((item) => (
						<button
							key={item.id}
							type="button"
							title={item.label}
							onClick={item.onClick}
							className={navClass(item.active)}
						>
							<svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
							</svg>
							<span className="text-sm truncate">{item.label}</span>
						</button>
					))}
				</nav>

				<div className="shrink-0 p-2 border-t border-slate-100 bg-slate-50/50 space-y-0.5">
					<div className="flex items-center gap-2 px-1 py-1.5">
						<div className="w-9 h-9 shrink-0 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
							<span className="text-white font-semibold text-sm">{user?.name?.charAt(0)?.toUpperCase()}</span>
						</div>
						<div className="flex-1 min-w-0">
							<p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
							<p className="text-xs text-slate-500 truncate">{user?.email}</p>
						</div>
					</div>
					<button
						type="button"
						onClick={() => navigate(`/${tenant}/my-subscription`)}
						title="Subscription"
						className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-slate-600 hover:bg-slate-100 transition-colors"
					>
						<svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
						</svg>
						<span className="text-sm">Subscription</span>
					</button>
					<button
						type="button"
						onClick={() => navigate(`/${tenant}/billing`)}
						title="Billing"
						className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 transition-colors ${
							onBilling ? 'bg-indigo-50 text-indigo-700 font-medium shadow-sm' : 'text-slate-600 hover:bg-slate-100'
						}`}
					>
						<svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
						</svg>
						<span className="text-sm">Billing</span>
					</button>
					<button
						type="button"
						onClick={() => setShowChangePasswordModal(true)}
						title="Change password"
						className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-slate-600 hover:bg-slate-100 transition-colors"
					>
						<svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
						</svg>
						<span className="text-sm">Change password</span>
					</button>
					<button
						type="button"
						onClick={handleLogout}
						title="Logout"
						className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-red-600 hover:bg-red-50 transition-colors"
					>
						<svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
						</svg>
						<span className="text-sm">Logout</span>
					</button>
				</div>
			</aside>

			<div className="flex-1 min-h-screen ml-52">{children ?? <Outlet />}</div>

			<AlertDialog
				isOpen={alert.isOpen}
				onClose={() => setAlert((a) => ({ ...a, isOpen: false }))}
				title={alert.title}
				message={alert.message}
				type={alert.type}
			/>
			<ChangePasswordModal
				isOpen={showChangePasswordModal}
				onClose={() => setShowChangePasswordModal(false)}
				onSuccess={() =>
					setAlert({
						isOpen: true,
						title: 'Success',
						message: 'Password updated successfully.',
						type: 'success'
					})
				}
			/>
		</div>
	);
};

export default InvestorLayout;
